import { useEffect, useState } from 'react';
import { Link, useNavigate, useSearchParams } from 'react-router';
import { useTranslation } from 'react-i18next';
import type { EmailOtpType } from '@supabase/supabase-js';
import { exchangeAuthCode, verifyEmailOtp, getSession } from '@/core/auth/supabase-auth';
import BrandLoading from '@/shared/components/feedback/BrandLoading';
import { ROUTES } from '../../constants/routes';

type Status = 'verifying' | 'error';

const AuthConfirmPage = () => {
  const { t } = useTranslation('auth');
  const navigate = useNavigate();
  const [params] = useSearchParams();
  const [status, setStatus] = useState<Status>('verifying');
  const [errorKey, setErrorKey] = useState<'errorMissingCode' | 'errorGeneric'>(
    'errorGeneric'
  );

  useEffect(() => {
    let cancelled = false;
    const code = params.get('code');
    // Custom Send Email Hook links use ?token_hash=&type= instead of PKCE ?code=.
    const tokenHash = params.get('token_hash');
    const type = params.get('type') as EmailOtpType | null;

    // If a session already exists (double-click on email link, StrictMode
    // double-effect, or this tab was opened after auth completed in another),
    // skip the exchange and go to the app — the user IS authenticated.
    const proceedToAppIfAuthed = async () => {
      const session = await getSession().catch(() => null);
      if (cancelled) return true;
      if (session) {
        navigate(ROUTES.STANDARDISIERUNG, { replace: true });
        return true;
      }
      return false;
    };

    (async () => {
      if (await proceedToAppIfAuthed()) return;

      if (!code && !(tokenHash && type)) {
        setErrorKey('errorMissingCode');
        setStatus('error');
        return;
      }

      try {
        if (code) {
          await exchangeAuthCode(code);
        } else if (tokenHash && type) {
          await verifyEmailOtp(tokenHash, type);
        }
        if (cancelled) return;
        navigate(ROUTES.STANDARDISIERUNG, { replace: true });
      } catch {
        if (cancelled) return;
        // Exchange failed (often: code already consumed). Re-check session —
        // if a parallel exchange already succeeded we should still treat this
        // as success rather than show a misleading error.
        if (await proceedToAppIfAuthed()) return;
        setErrorKey('errorGeneric');
        setStatus('error');
      }
    })();

    return () => {
      cancelled = true;
    };
  }, [params, navigate]);

  if (status === 'verifying') {
    return <BrandLoading loadingText={t('authConfirm.verifying')} />;
  }

  return (
    <div className="flex items-center justify-center min-h-screen bg-background px-4">
      <div className="w-full max-w-md p-6 sm:p-8 space-y-6 bg-card rounded-lg shadow-md text-center">
        <div className="flex justify-center">
          <img src="/logo-header-light.svg" alt="Forecast Engine" className="h-16 w-auto" />
        </div>
        <h1 className="text-xl font-bold text-foreground">
          {t('authConfirm.errorTitle')}
        </h1>
        <p className="text-muted-foreground text-sm">{t(`authConfirm.${errorKey}`)}</p>
        <Link
          to="/login"
          className="inline-flex items-center justify-center w-full px-4 py-2 text-sm font-medium text-primary-foreground rounded-md shadow-sm bg-primary hover:bg-primary/90 focus:outline-none focus:ring-2 focus:ring-ring focus:ring-offset-2"
        >
          {t('authConfirm.backToLogin')}
        </Link>
      </div>
    </div>
  );
};

export default AuthConfirmPage;
